// Video Provider Configuration

export const PROVIDER_TYPES = {
    AGORA: 'agora',
    ZOOM: 'zoom',
    WEBRTC: 'webrtc',
    JITSI: 'jitsi',
    CUSTOM: 'custom'
};

export const PROVIDER_CONFIGS = {
    [PROVIDER_TYPES.AGORA]: {
        label: 'Agora',
        icon: 'Videocam',
        description: 'Real-time engagement platform for video, voice, and live interactive streaming',
        fields: {
            appId: {
                label: 'App ID',
                type: 'text',
                required: true,
                description: 'Agora App ID from Agora Console'
            },
            appCertificate: {
                label: 'App Certificate',
                type: 'password',
                required: true,
                description: 'Agora App Certificate for token generation'
            },
            customerId: {
                label: 'Customer ID',
                type: 'text',
                required: false,
                description: 'Agora Customer ID (optional)'
            },
            customerSecret: {
                label: 'Customer Secret',
                type: 'password',
                required: false,
                description: 'Agora Customer Secret (optional)'
            },
            region: {
                label: 'Region',
                type: 'select',
                required: true,
                options: ['CN', 'AP', 'EU', 'NA', 'SA'],
                default: 'AP'
            },
            channelProfile: {
                label: 'Channel Profile',
                type: 'select',
                required: true,
                options: ['Communication', 'Live Broadcast', 'Gaming'],
                default: 'Communication'
            },
            tokenExpiration: {
                label: 'Token Expiration (hours)',
                type: 'number',
                required: true,
                default: 24,
                min: 1,
                max: 168
            }
        },
        features: [
            'HD Video (1080p)',
            'Screen Sharing',
            'Cloud Recording',
            'Live Streaming',
            'Whiteboard',
            'Polls & Surveys',
            'Breakout Rooms',
            'Voice Effects',
            'Background Blur',
            'Virtual Backgrounds'
        ],
        limits: {
            maxParticipants: 100,
            maxDuration: 0,
            recordingStorage: '50GB'
        }
    },
    [PROVIDER_TYPES.ZOOM]: {
        label: 'Zoom',
        icon: 'VideoSettings',
        description: 'Enterprise video communications platform for meetings, webinars, and chat',
        fields: {
            accountId: {
                label: 'Account ID',
                type: 'text',
                required: true,
                description: 'Zoom Account ID from Zoom Marketplace'
            },
            clientId: {
                label: 'Client ID',
                type: 'text',
                required: true,
                description: 'Zoom OAuth Client ID'
            },
            clientSecret: {
                label: 'Client Secret',
                type: 'password',
                required: true,
                description: 'Zoom OAuth Client Secret'
            },
            verificationToken: {
                label: 'Verification Token',
                type: 'text',
                required: false,
                description: 'Zoom Webhook Verification Token'
            },
            webhookSecret: {
                label: 'Webhook Secret',
                type: 'password',
                required: false,
                description: 'Zoom Webhook Secret Token'
            }
        },
        features: [
            'HD Video (1080p)',
            'Screen Sharing',
            'Cloud Recording',
            'Live Streaming',
            'Whiteboard',
            'Polls & Surveys',
            'Breakout Rooms',
            'Webinars',
            'Chat',
            'Virtual Backgrounds'
        ],
        limits: {
            maxParticipants: 1000,
            maxDuration: 40,
            recordingStorage: 'Unlimited'
        }
    },
    [PROVIDER_TYPES.WEBRTC]: {
        label: 'WebRTC (Self-Hosted)',
        icon: 'NetworkCheck',
        description: 'Open-source peer-to-peer real-time communication',
        fields: {
            signalingServer: {
                label: 'Signaling Server URL',
                type: 'text',
                required: true,
                description: 'WebSocket signaling server endpoint'
            },
            stunServer: {
                label: 'STUN Server',
                type: 'text',
                required: false,
                default: 'stun:stun.l.google.com:19302',
                description: 'STUN server for NAT traversal'
            },
            turnServer: {
                label: 'TURN Server',
                type: 'text',
                required: false,
                description: 'TURN server for media relay'
            },
            turnUsername: {
                label: 'TURN Username',
                type: 'text',
                required: false,
                description: 'TURN server username'
            },
            turnPassword: {
                label: 'TURN Password',
                type: 'password',
                required: false,
                description: 'TURN server password'
            }
        },
        features: [
            'Peer-to-Peer Video',
            'Screen Sharing',
            'Data Channels',
            'Low Latency',
            'End-to-End Encryption',
            'Customizable',
            'No Third Party Costs'
        ],
        limits: {
            maxParticipants: 50,
            maxDuration: 0,
            recordingStorage: 'Self-hosted'
        }
    },
    [PROVIDER_TYPES.JITSI]: {
        label: 'Jitsi Meet',
        icon: 'People',
        description: 'Open-source video conferencing platform with self-hosting option',
        fields: {
            serverUrl: {
                label: 'Server URL',
                type: 'text',
                required: true,
                default: 'https://meet.jit.si',
                description: 'Jitsi server URL (self-hosted or public)'
            },
            apiKey: {
                label: 'API Key',
                type: 'text',
                required: false,
                description: 'Jitsi API Key (for self-hosted)'
            },
            jwtSecret: {
                label: 'JWT Secret',
                type: 'password',
                required: false,
                description: 'JWT secret for authentication'
            },
            roomPrefix: {
                label: 'Room Prefix',
                type: 'text',
                required: false,
                default: 'telcofair',
                description: 'Prefix for meeting room IDs'
            }
        },
        features: [
            'HD Video (1080p)',
            'Screen Sharing',
            'Live Streaming',
            'Whiteboard',
            'Polls & Surveys',
            'Breakout Rooms',
            'End-to-End Encryption',
            'Virtual Backgrounds',
            'Open Source'
        ],
        limits: {
            maxParticipants: 100,
            maxDuration: 0,
            recordingStorage: 'Self-hosted or Cloud'
        }
    },
    [PROVIDER_TYPES.CUSTOM]: {
        label: 'Custom Provider',
        icon: 'Settings',
        description: 'Custom video provider integration',
        fields: {
            serverUrl: {
                label: 'Server URL',
                type: 'text',
                required: true,
                description: 'Custom provider server URL'
            },
            apiKey: {
                label: 'API Key',
                type: 'text',
                required: true,
                description: 'API key for authentication'
            },
            apiSecret: {
                label: 'API Secret',
                type: 'password',
                required: true,
                description: 'API secret for authentication'
            },
            webhookUrl: {
                label: 'Webhook URL',
                type: 'text',
                required: false,
                description: 'Webhook URL for events'
            }
        },
        features: [
            'Custom Integration',
            'Flexible Configuration',
            'Full Control'
        ],
        limits: {
            maxParticipants: 0,
            maxDuration: 0,
            recordingStorage: 'Custom'
        }
    }
};

// Quality Profiles
export const QUALITY_PROFILES = {
    LOW: {
        id: 'low',
        label: 'Low (240p)',
        resolution: '240p',
        bitrate: '200kbps',
        frameRate: 15,
        audioBitrate: '32kbps'
    },
    SD: {
        id: 'sd',
        label: 'SD (480p)',
        resolution: '480p',
        bitrate: '500kbps',
        frameRate: 25,
        audioBitrate: '64kbps'
    },
    HD: {
        id: 'hd',
        label: 'HD (720p)',
        resolution: '720p',
        bitrate: '1.5Mbps',
        frameRate: 30,
        audioBitrate: '96kbps'
    },
    FULL_HD: {
        id: 'full_hd',
        label: 'Full HD (1080p)',
        resolution: '1080p',
        bitrate: '3Mbps',
        frameRate: 30,
        audioBitrate: '128kbps'
    },
    UHD: {
        id: 'uhd',
        label: 'UHD (4K)',
        resolution: '2160p',
        bitrate: '6Mbps',
        frameRate: 60,
        audioBitrate: '256kbps'
    }
};

// Recording Settings
export const RECORDING_SETTINGS = {
    CLOUD: {
        id: 'cloud',
        label: 'Cloud Recording',
        description: 'Store recordings in provider cloud storage',
        features: ['Auto-save', 'Cloud storage', 'Multi-format export', 'Share links']
    },
    LOCAL: {
        id: 'local',
        label: 'Local Recording',
        description: 'Record and save locally to your device',
        features: ['Full control', 'No cloud costs', 'Privacy', 'Quick access']
    },
    HYBRID: {
        id: 'hybrid',
        label: 'Hybrid',
        description: 'Record locally with cloud backup',
        features: ['Local + Cloud', 'Redundancy', 'Backup', 'Easy sharing']
    }
};

// Video Codecs
export const VIDEO_CODECS = [
    { id: 'h264', label: 'H.264', description: 'Most compatible, good quality' },
    { id: 'h265', label: 'H.265/HEVC', description: 'Better compression, newer' },
    { id: 'vp8', label: 'VP8', description: 'Open source, good quality' },
    { id: 'vp9', label: 'VP9', description: 'Open source, better compression' },
    { id: 'av1', label: 'AV1', description: 'Latest, best compression' }
];

// Audio Codecs
export const AUDIO_CODECS = [
    { id: 'opus', label: 'Opus', description: 'Best quality, open source' },
    { id: 'aac', label: 'AAC', description: 'Good quality, widely supported' },
    { id: 'pcmu', label: 'PCMU', description: 'Simple, low latency' },
    { id: 'pcma', label: 'PCMA', description: 'Simple, low latency' }
];

// Default export for easier importing
export default {
    PROVIDER_TYPES,
    PROVIDER_CONFIGS,
    QUALITY_PROFILES,
    RECORDING_SETTINGS,
    VIDEO_CODECS,
    AUDIO_CODECS
};